/* @jsx h */
import {
  defineComponent,
  h,
  onMounted,
  onUnmounted,
  ref,
  Teleport,
  type PropType,
} from "vue";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue";
import type { GridColumnDef, CommitDir } from "./types";
import { ColorPicker } from "../ColorPicker";
import { nearestColorName } from "./utils/colorName";

export const ColorEditor = defineComponent({
  name: "ColorEditor",
  props: {
    column: { type: Object as PropType<GridColumnDef>, required: true },
    value: { type: String as PropType<string | null>, default: null },
    onCommit: {
      type: Function as PropType<(value: any, dir: CommitDir) => void>,
      required: true,
    },
    onCancel: { type: Function as PropType<() => void>, required: true },
  },
  setup(props) {
    const anchorEl = ref<HTMLElement | null>(null);
    const panelEl = ref<HTMLElement | null>(null);

    const { floatingStyles } = useFloating(anchorEl, panelEl, {
      placement: "bottom-start",
      whileElementsMounted: autoUpdate,
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    });

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (anchorEl.value?.contains(target) || panelEl.value?.contains(target)) return;
      props.onCancel();
    };

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        props.onCancel();
      }
    };

    onMounted(() => {
      document.addEventListener("pointerdown", handlePointerDown, { capture: true });
      document.addEventListener("keydown", handleKeydown, { capture: true });
    });

    onUnmounted(() => {
      document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
      document.removeEventListener("keydown", handleKeydown, { capture: true });
    });

    return () => (
      <>
        {/* Anchor fills the cell so floating-ui has the correct reference bounds,
            and shows the current color while the picker is open */}
        <div ref={anchorEl} class="lg-color-anchor">
          {props.value ? (
            <span class="lg-cell-color">
              <span class="lg-cell-color-swatch" style={{ backgroundColor: props.value }} />
              <span class="lg-cell-text">{nearestColorName(props.value) ?? props.value}</span>
            </span>
          ) : (
            <span class="lg-cell-text lg-cell-placeholder">—</span>
          )}
        </div>
        <Teleport to="body">
          <div ref={panelEl} class="lg-color-editor-panel" style={floatingStyles.value}>
            <ColorPicker
              value={props.value}
              presetColors={props.column.colorPresets ?? []}
              onChange={(color: string) => props.onCommit(color, null)}
            />
          </div>
        </Teleport>
      </>
    );
  },
});
